import { Id } from '../types'

type Dir = 'up' | 'down' | 'left' | 'right'

export type VirtualCell = {
    rowId: Id
    colId: Id
    r: number
    c: number
}

export type VirtualGrid = {
    matrix: VirtualCell[][]
    rowIndexById: Map<string, number>
    colIndexById: Map<string, number>

    move(from: VirtualCell, dir: Dir): VirtualCell | undefined
    jump(from: VirtualCell, dir: Dir): VirtualCell | undefined
    getBetween(from: VirtualCell, to: VirtualCell): VirtualCell[]
    getVirtualCell(rowId: Id, colId: Id): VirtualCell | undefined
    getRect(cells: VirtualCell[]): VirtualCell[]
}

export function buildVirtualGrid(rows: { id: Id }[], cols: { id: Id }[]): VirtualGrid {
    const rowIndexById = new Map<string, number>(rows.map((r, i) => [String(r.id), i] as const))
    const colIndexById = new Map<string, number>(cols.map((c, i) => [String(c.id), i] as const))

    const getVirtualCell = (rowId: Id, colId: Id): VirtualCell | undefined => {
        const r = rowIndexById.get(String(rowId))
        const c = colIndexById.get(String(colId))
        if (r === undefined || c === undefined) return
        const row = rows[r]
        const col = cols[c]
        if (!row || !col) return
        return { rowId: row.id, colId: col.id, r, c }
    }

    const at = (r: number, c: number): VirtualCell | undefined => {
        const row = rows[r]
        const col = cols[c]
        if (!row || !col) return
        return { rowId: row.id, colId: col.id, r, c }
    }

    const move = (from: VirtualCell, dir: Dir): VirtualCell | undefined => {
        let { r, c } = from
        if (dir === 'up') r--
        else if (dir === 'down') r++
        else if (dir === 'left') c--
        else c++
        return at(r, c)
    }

    const jump = (from: VirtualCell, dir: Dir): VirtualCell | undefined => {
        let { r, c } = from
        if (dir === 'up') r = 0
        else if (dir === 'down') r = rows.length - 1
        else if (dir === 'left') c = 0
        else c = cols.length - 1
        return at(r, c)
    }

    const getBetween = (from: VirtualCell, to: VirtualCell): VirtualCell[] => {
        if (from.r !== to.r) return []
        const r = from.r
        const start = Math.min(from.c, to.c)
        const end = Math.max(from.c, to.c)

        const out: VirtualCell[] = []
        for (let c = start; c <= end; c++) {
            const v = at(r, c)
            if (v) out.push(v)
        }
        return out
    }

    const getRect = (cells: VirtualCell[]): VirtualCell[] => {
        const [first] = cells
        if (!first) return []

        let rowStart = first.r
        let rowEnd = first.r
        let columnStart = first.c
        let columnEnd = first.c

        for (const cell of cells) {
            rowStart = Math.min(rowStart, cell.r)
            rowEnd = Math.max(rowEnd, cell.r)
            columnStart = Math.min(columnStart, cell.c)
            columnEnd = Math.max(columnEnd, cell.c)
        }

        const out: VirtualCell[] = []
        for (let r = rowStart; r <= rowEnd; r++) {
            for (let c = columnStart; c <= columnEnd; c++) {
                const v = at(r, c)
                if (v) {
                    out.push(v)
                }
            }
        }
        return out
    }

    const matrix: VirtualCell[][] = []

    return { matrix, rowIndexById, colIndexById, move, jump, getBetween, getVirtualCell, getRect }
}
